import { AlertTriangleIcon, BracesIcon, ChevronDownIcon } from "lucide-react";
import { useState } from "react";

import { isEvaluateInputColumn } from "../evaluate-drawer-utils";
import { useEvaluateScope } from "../useEvaluateScope";

import AutofillButton from "@/components/PowerBlock/Block/InputVariablesField/AutofillButton";
import { formatVariableName } from "@/components/PowerBlock/Block/InputVariablesField/types";
import SelectSource, {
  SourceType,
  type SourcesV2,
} from "@/components/PowerBlock/Block/SourcesField/SelectSource";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  PlaygroundEvaluateColumn,
  PlaygroundEvaluateSheetReference,
  useUpdateEvaluateSheetReference,
} from "@/queries/playground/evaluate";
import { ToastType } from "@/enums";
import { cn } from "@/lib/utils";
import { displayToast } from "@/utils/toast";

const createColumnSources = (
  columns: PlaygroundEvaluateColumn[],
): { sources: Record<SourceType, string[]>; sourcesV2: SourcesV2 } => ({
  sources: {
    [SourceType.Column]: columns.map(column => column.id),
    [SourceType.Dataset]: [],
    [SourceType.InputVariable]: [],
    [SourceType.Dependency]: [],
  },
  sourcesV2: {
    [SourceType.Column]: columns.map(column => ({
      id: column.id,
      label: column.title,
      value: column.id,
    })),
  },
});

interface MappingSelectProps {
  variable: string;
  value: string;
  columns: PlaygroundEvaluateColumn[];
  unavailableColumnIds: string[];
  hasMissingValues: boolean;
  disabled: boolean;
  onChange: (columnId: string) => void;
}

const MappingSelect = ({
  variable,
  value,
  columns,
  unavailableColumnIds,
  hasMissingValues,
  disabled,
  onChange,
}: MappingSelectProps) => {
  const availableColumns = columns.filter(
    column => column.id === value || !unavailableColumnIds.includes(column.id),
  );
  const { sources, sourcesV2 } = createColumnSources(availableColumns);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="input-variable break-all text-xs">
          {formatVariableName(variable, "f-string")}
        </div>
        {hasMissingValues && (
          <span className="shrink-0 text-[10px] font-medium text-amber-600">Needs value</span>
        )}
      </div>
      <div className="min-w-0 rounded-md border border-gray-200 bg-gray-50 p-2.5">
        <SelectSource
          value={value || undefined}
          sources={sources}
          sourcesV2={sourcesV2}
          onValueChange={onChange}
          placeholder="Select a sheet column..."
          disabled={disabled || columns.length === 0}
        />
      </div>
    </div>
  );
};

interface VariableMappingsControlProps {
  variableNames: string[];
  reference: PlaygroundEvaluateSheetReference;
  mappedColumnIds: string[];
  hasDuplicateMappings: boolean;
  unresolvedVariableNames: string[];
  disabled: boolean;
}

const VariableMappingsControl = ({
  variableNames,
  reference,
  mappedColumnIds,
  hasDuplicateMappings,
  unresolvedVariableNames,
  disabled,
}: VariableMappingsControlProps) => {
  const { queryScope } = useEvaluateScope();
  const updateReference = useUpdateEvaluateSheetReference(queryScope);
  const [open, setOpen] = useState(false);
  const mappableColumns = reference.columns.filter(isEvaluateInputColumn);
  const mappableColumnIds = new Set(mappableColumns.map(column => column.id));
  const columnUseCounts = new Map<string, number>();
  mappedColumnIds.forEach(columnId => {
    columnUseCounts.set(columnId, (columnUseCounts.get(columnId) ?? 0) + 1);
  });
  const autofillVariables = variableNames.filter(variable => {
    const columnId = reference.input_variable_mappings[variable]?.column_id;
    return (
      !columnId || !mappableColumnIds.has(columnId) || (columnUseCounts.get(columnId) ?? 0) > 1
    );
  });
  const reservedColumnIds = new Set(
    variableNames.flatMap(variable => {
      const columnId = reference.input_variable_mappings[variable]?.column_id;
      return columnId && columnUseCounts.get(columnId) === 1 ? [columnId] : [];
    }),
  );
  const autofillColumns = mappableColumns.filter(column => !reservedColumnIds.has(column.id));
  const autofillSources = createColumnSources(autofillColumns);
  const mappedCount = variableNames.length - autofillVariables.length;
  const currentMappings = Object.fromEntries(
    Object.entries(reference.input_variable_mappings).flatMap(([variable, mapping]) =>
      mappableColumnIds.has(mapping.column_id) ? [[variable, mapping.column_id]] : [],
    ),
  );

  const saveMappings = (mappings: Record<string, string>, onSuccess?: () => void) => {
    updateReference.mutate(
      {
        referenceId: reference.id,
        body: {
          input_variable_mappings: Object.fromEntries(
            Object.entries(mappings).map(([variable, columnId]) => [
              variable,
              { column_id: columnId },
            ]),
          ),
        },
      },
      { onSuccess },
    );
  };

  const handleAutofill = (suggestedMappings: Record<string, string>) => {
    const nextMappings = { ...currentMappings };
    autofillVariables.forEach(variable => delete nextMappings[variable]);

    const usedColumnIds = new Set(Object.values(nextMappings));
    let mappedVariables = 0;
    autofillVariables.forEach(variable => {
      const columnId = suggestedMappings[variable];
      if (!columnId || usedColumnIds.has(columnId)) {
        return;
      }
      nextMappings[variable] = columnId;
      usedColumnIds.add(columnId);
      mappedVariables += 1;
    });

    if (mappedVariables === 0) {
      displayToast("No suitable column matches found", ToastType.warning);
      return;
    }

    saveMappings(nextMappings, () => {
      displayToast(
        `Auto-mapped ${mappedVariables} input${mappedVariables === 1 ? "" : "s"}`,
        ToastType.success,
      );
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 max-w-full items-center justify-start gap-1.5 rounded-full border border-gray-200/70 bg-gray-50/80 px-2.5 text-sm font-medium leading-none text-gray-600 shadow-none transition-colors hover:border-gray-300 hover:bg-gray-100/80 hover:text-gray-900 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 data-[state=open]:border-gray-300 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900"
          aria-label={`${mappedCount} of ${variableNames.length} input variables mapped`}
          title="Map input variables to sheet columns"
        >
          <BracesIcon className="h-4 w-4 shrink-0 text-gray-400" />
          <span className="leading-5 text-gray-700">Variables</span>
          <span
            className={cn(
              "text-xs font-medium tabular-nums",
              hasDuplicateMappings || unresolvedVariableNames.length > 0
                ? "text-amber-600"
                : "text-gray-400",
            )}
          >
            {mappedCount}/{variableNames.length}
          </span>
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        avoidCollisions
        collisionPadding={12}
        sticky="always"
        className="mx-0 overflow-y-auto overscroll-contain rounded-xl border-gray-200 p-0 shadow-lg"
        style={{
          width: "min(420px, calc(100vw - 24px), var(--radix-popover-content-available-width))",
          maxHeight:
            "min(80dvh, calc(100dvh - 24px), var(--radix-popover-content-available-height))",
        }}
        onWheel={event => event.stopPropagation()}
      >
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-3 py-2.5">
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-800">Map input variables</div>
            <p className="mt-0.5 break-words text-xs leading-5 text-gray-500">
              Input-set values override mapped sheet columns. Empty values use the corresponding
              sheet value.
            </p>
            {unresolvedVariableNames.length > 0 && (
              <p className="mt-1 break-words text-[11px] leading-4 text-amber-700">
                {unresolvedVariableNames.length} input
                {unresolvedVariableNames.length === 1 ? " needs" : "s need"} a sheet mapping or a
                value in every input set.
              </p>
            )}
          </div>
          {variableNames.length > 0 && (
            <AutofillButton
              variables={autofillVariables}
              sources={autofillSources.sources}
              sourcesV2={autofillSources.sourcesV2}
              onAutofill={handleAutofill}
              disabled={disabled || updateReference.isPending}
              readonly={disabled}
            />
          )}
        </div>
        {variableNames.length === 0 ? (
          <div className="break-words p-3 text-xs leading-5 text-gray-500">
            Add an input variable to the prompt before mapping it to this sheet.
          </div>
        ) : (
          <div className="space-y-3 p-3">
            {hasDuplicateMappings && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200/80 bg-amber-50/70 px-2.5 py-2 text-amber-900">
                <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                <div className="text-[11px] leading-4 text-amber-800">
                  Map each input variable to a different text column.
                </div>
              </div>
            )}
            <div className="grid gap-3">
              {variableNames.map(variable => (
                <MappingSelect
                  key={variable}
                  variable={variable}
                  value={
                    mappableColumnIds.has(
                      reference.input_variable_mappings[variable]?.column_id || "",
                    )
                      ? reference.input_variable_mappings[variable]?.column_id || ""
                      : ""
                  }
                  columns={mappableColumns}
                  unavailableColumnIds={mappedColumnIds}
                  hasMissingValues={unresolvedVariableNames.includes(variable)}
                  disabled={disabled || updateReference.isPending}
                  onChange={columnId => {
                    const nextMappings = { ...currentMappings };
                    if (columnId) {
                      nextMappings[variable] = columnId;
                    } else {
                      delete nextMappings[variable];
                    }
                    saveMappings(nextMappings);
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default VariableMappingsControl;
